<template>
    <div v-if="album" class="listener-album">
        <div class="listener-album__backdrop" :style="albumBackdropStyle"></div>
        <div class="listener-album__glow"></div>

        <div class="listener-album__shell">
            <div class="listener-album__topbar">
                <button class="listener-album__icon-button" @click="goBack">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M15 18 9 12l6-6"></path>
                    </svg>
                </button>
                <div class="listener-album__heading">
                    <div class="listener-album__eyebrow">{{ $tt('专辑详情') }}</div>
                    <div class="listener-album__caption">{{ album.artist || $tt('未知艺术家') }}</div>
                </div>
                <button class="listener-album__icon-button" @click="addFavouriteAlbum">
                    <svg viewBox="0 0 24 24" :fill="isFavourite ? '#ef4444' : 'none'" stroke="currentColor" stroke-width="2">
                        <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                    </svg>
                </button>
            </div>

            <div class="listener-album__display-switch-wrap">
                <div class="listener-album__display-switch">
                    <button
                        :class="['listener-album__display-pill', !showOverviewPanel ? 'is-active' : '']"
                        @click="setDisplayMode(false)"
                    >
                        {{ $tt('封面') }}
                    </button>
                    <button
                        :class="['listener-album__display-pill', showOverviewPanel ? 'is-active' : '']"
                        @click="setDisplayMode(true)"
                    >
                        {{ $tt('概览') }}
                    </button>
                </div>
            </div>

            <section class="listener-album__hero">
                <div class="listener-album__display-stage">
                    <div v-if="!showOverviewPanel" class="listener-album__art-stage">
                        <div class="listener-album__vinyl"></div>
                        <div class="listener-album__art-card">
                            <img
                                :src="album.image || '/static/dist/img/album_null.jpg'"
                                :alt="album.name"
                                class="listener-album__art-image"
                            >
                        </div>
                    </div>
                    <div v-else class="listener-album__overview-stage">
                        <div class="listener-album__overview-card">
                            <div class="listener-album__overview-label">{{ $tt('简介') }}</div>
                            <p class="listener-album__overview-text">
                                {{ album.comment || $tt('暂无简介') }}
                            </p>
                        </div>

                        <div class="listener-album__overview-card">
                            <div class="listener-album__overview-label">{{ $tt('专辑信息') }}</div>
                            <div class="listener-album__info-list">
                                <div class="listener-album__info-row">
                                    <span>{{ $tt('曲目') }}</span>
                                    <strong>{{ trackCount }}</strong>
                                </div>
                                <div class="listener-album__info-row">
                                    <span>{{ $tt('曲目时长') }}</span>
                                    <strong>{{ formatDuration(album.duration) }}</strong>
                                </div>
                                <div class="listener-album__info-row">
                                    <span>{{ $tt('艺术家') }}</span>
                                    <router-link :to="{ name: 'artist', params: { id: album.artistId } }">
                                        {{ album.artist || '--' }}
                                    </router-link>
                                </div>
                                <div class="listener-album__info-row">
                                    <span>{{ $tt('年份') }}</span>
                                    <strong>{{ album.year || '--' }}</strong>
                                </div>
                                <div class="listener-album__info-row">
                                    <span>{{ $tt('时间') }}</span>
                                    <strong>{{ formatDate(album.created) }}</strong>
                                </div>
                                <div class="listener-album__info-row">
                                    <span>{{ $tt('流派') }}</span>
                                    <router-link v-if="album.genreId" :to="{ name: 'genre', params: { id: album.genreId } }">
                                        {{ album.genreId }}
                                    </router-link>
                                    <strong v-else>--</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="listener-album__meta">
                    <div class="listener-album__meta-kicker">{{ $tt('专辑') }}</div>
                    <h1 class="listener-album__title">{{ album.name }}</h1>
                    <router-link
                        :to="{ name: 'artist', params: { id: album.artistId } }"
                        class="listener-album__artist"
                    >
                        {{ album.artist || $tt('未知艺术家') }}
                    </router-link>

                    <p v-if="album.comment" class="listener-album__summary" @click="openCommentModal">
                        {{ albumCommentPreview }}
                    </p>
                    <div class="listener-album__actions">
                        <button class="listener-album__play-button" @click="playNow(album.id)">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                            </svg>
                            <span>{{ $tt('播放') }}</span>
                        </button>
                        <button class="listener-album__action-button" @click="shuffleNow">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17 3h4v4h-2V6.41l-4.59 4.58-1.41-1.41L17.59 5H17V3zM3 6h2.59l5.29 5.29-1.41 1.41L4.41 8H3V6zm10.53 5.47 1.41 1.41L12.41 15.4l1.06 1.06-1.41 1.41L9.59 15.4 3 22v-2.83l5.17-5.17L3 8.83V6l10.53 10.47zM17 17h.59l-4.59-4.58 1.41-1.41L19 15.59V15h2v4h-4v-2z"></path>
                            </svg>
                            <span>{{ $tt('随机') }}</span>
                        </button>
                        <button class="listener-album__action-button" @click="setNextInQueue">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 5h10v2H4zm0 6h10v2H4zm0 6h10v2H4zm12-7V6l6 5-6 5v-4z"></path>
                            </svg>
                            <span>{{ $tt('下一首') }}</span>
                        </button>
                        <button class="listener-album__action-button" @click="addToQueue">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 6h12v2H4zm0 5h12v2H4zm0 5h8v2H4zm14-5V8h2v3h3v2h-3v3h-2v-3h-3v-2z"></path>
                            </svg>
                            <span>{{ $tt('队列') }}</span>
                        </button>
                    </div>
                </div>
            </section>

            <section class="listener-album__tracks">
                <div class="listener-album__panel-header">
                    <div>
                        <div class="listener-album__panel-eyebrow">{{ $tt('曲目列表') }}</div>
                        <h2 class="listener-album__panel-title">{{ $tt('{count} 首歌曲', { count: trackCount }) }}</h2>
                    </div>
                </div>
                <ListenerTrackList :tracks="album.tracks" no-album />
            </section>
        </div>

        <dialog ref="albumCommentModal" class="modal">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('专辑简介:') }}{{ album.name }}</h3>
                <div class="py-4 whitespace-pre-wrap break-words text-base-content/80">
                    {{ album.comment || $tt('暂无简介') }}
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>
    </div>
</template>

<script>
import ListenerTrackList from '@/views/library/track/ListenerTrackList.vue'

export default {
    name: 'ListenerAlbumDetails',
    components: {
        ListenerTrackList
    },
    data() {
        return {
            album: null,
            id: this.$route.params.id,
            isFavourite: false,
            showAllArtists: false,
            maxDisplayedArtists: 3,
            showOverviewPanel: false
        }
    },
    computed: {
        displayedOtherArtists() {
            if (!this.album?.otherArtist || this.album.otherArtist.length === 0) {
                return []
            }
            if (this.showAllArtists || this.album.otherArtist.length <= this.maxDisplayedArtists) {
                return this.album.otherArtist
            }
            return this.album.otherArtist.slice(0, this.maxDisplayedArtists)
        },
        hasMoreArtists() {
            return this.album?.otherArtist && this.album.otherArtist.length > this.maxDisplayedArtists
        },
        remainingArtistsCount() {
            if (!this.album?.otherArtist) return 0
            return this.album.otherArtist.length - this.maxDisplayedArtists
        },
        trackCount() {
            return this.album?.tracks?.length || 0
        },
        albumCommentPreview() {
            if (!this.album?.comment) return ''
            return this.album.comment.length > 120 ? `${this.album.comment.slice(0, 120)}...` : this.album.comment
        },
        albumBackdropStyle() {
            return {
                backgroundImage: `url('${this.album?.image || '/static/dist/img/album_null.jpg'}')`
            }
        }
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            handler(value) {
                this.loadAlbum(value)
            }
        }
    },
    methods: {
        async loadAlbum(id) {
            this.id = id
            this.album = await this.$api.subsonic.getAlbumDetails(id)
            this.isFavourite = !!this.album.favourite
            this.showAllArtists = false
            this.showOverviewPanel = false
        },
        async playNow(id) {
            if (this.album && String(id) === String(this.album.id)) {
                return this.$store.dispatch('playTrackList', {
                    tracks: this.album.tracks
                })
            }
            const album = await this.$api.subsonic.getAlbumDetails(id)
            return this.$store.dispatch('playTrackList', {
                tracks: album.tracks
            })
        },
        shuffleNow() {
            return this.$store.dispatch('player/shuffleNow', {
                tracks: this.album && this.album.tracks ? this.album.tracks : []
            })
        },
        setNextInQueue() {
            if (this.album) {
                return this.$store.dispatch('player/setNextInQueue', this.album.tracks)
            }
        },
        addToQueue() {
            if (this.album) {
                return this.$store.dispatch('player/addToQueue', this.album.tracks)
            }
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.back()
                return
            }
            this.$router.push({ path: '/albums/recently-added/' })
        },
        setDisplayMode(isOverview) {
            this.showOverviewPanel = isOverview
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
        },
        formatDate(value) {
            if (!value) return '--'
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) {
                return value
            }
            const year = date.getFullYear()
            const month = `${date.getMonth() + 1}`.padStart(2, '0')
            const day = `${date.getDate()}`.padStart(2, '0')
            return `${year}-${month}-${day}`
        },
        addFavouriteAlbum() {
            if (this.isFavourite) {
                this.$api.subsonic.removeFavourite(this.album.id, 'album').then(() => {
                    this.isFavourite = false
                })
            } else {
                this.$api.subsonic.addFavourite(this.album.id, 'album').then(() => {
                    this.isFavourite = true
                })
            }
        },
        openCommentModal() {
            if (this.$refs.albumCommentModal) {
                this.$refs.albumCommentModal.showModal()
            }
        },
        toggleShowAllArtists() {
            this.showAllArtists = !this.showAllArtists
        }
    }
}
</script>

<style scoped>
.listener-album {
    --display-stage-size: min(19rem, 78vw, 34dvh);
    position: relative;
    height: calc(100dvh - var(--listener-shell-dock-height, 12.25rem));
    box-sizing: border-box;
    padding: 0.85rem 0.9rem 2rem;
    color: #0f172a;
    background:
        radial-gradient(circle at top, rgba(255, 255, 255, 0.92), rgba(244, 247, 251, 0.94) 48%, rgba(241, 245, 249, 0.98) 100%);
    overflow-y: auto;
    overflow-x: hidden;
}

.listener-album__backdrop {
    position: absolute;
    inset: 0;
    background-position: center top;
    background-size: cover;
    filter: blur(44px);
    opacity: 0.18;
    transform: scale(1.08);
    pointer-events: none;
}

.listener-album__glow {
    position: absolute;
    top: -4rem;
    left: 50%;
    width: 22rem;
    height: 22rem;
    border-radius: 999px;
    transform: translateX(-50%);
    background: radial-gradient(circle, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0));
    pointer-events: none;
}

.listener-album__shell {
    position: relative;
    z-index: 1;
}

.listener-album__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.listener-album__heading {
    flex: 1;
    text-align: center;
    min-width: 0;
}

.listener-album__eyebrow {
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-album__caption {
    margin-top: 0.12rem;
    font-size: 0.84rem;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-album__icon-button {
    width: 2.8rem;
    height: 2.8rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.84);
    color: #111827;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.16);
    flex-shrink: 0;
}

.listener-album__icon-button svg {
    width: 1.2rem;
    height: 1.2rem;
}

.listener-album__hero {
    display: flex;
    flex-direction: column;
}

.listener-album__display-stage {
    height: var(--display-stage-size);
    margin-top: 1.25rem;
    margin-bottom: 1.45rem;
    display: flex;
    align-items: stretch;
}

.listener-album__art-stage {
    position: relative;
    margin: 0 auto;
    width: var(--display-stage-size);
    height: var(--display-stage-size);
    align-self: center;
}

.listener-album__overview-stage {
    width: 100%;
    height: 100%;
    display: grid;
    align-content: start;
    gap: 0.9rem;
    overflow-y: auto;
    padding-right: 0.1rem;
}

.listener-album__vinyl {
    position: absolute;
    right: -1.5rem;
    top: 1.7rem;
    width: 72%;
    height: 72%;
    border-radius: 999px;
    background:
        radial-gradient(circle at center, #0f172a 0 10%, #111827 10% 15%, #020617 15% 52%, #111827 52% 60%, #020617 60% 100%);
    box-shadow: 0 22px 50px rgba(15, 23, 42, 0.22);
}

.listener-album__art-card {
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;
    padding: 0.8rem;
    border-radius: 2rem;
    background: linear-gradient(180deg, rgba(255,255,255,0.72), rgba(255,255,255,0.36));
    backdrop-filter: blur(20px);
    box-shadow: 0 28px 70px rgba(99, 102, 241, 0.18);
}

.listener-album__art-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 1.45rem;
}

.listener-album__meta-kicker {
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #94a3b8;
}

.listener-album__title {
    margin-top: 0.5rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #111827;
    word-break: break-word;
}

.listener-album__artist {
    margin-top: 0.65rem;
    display: inline-block;
    font-size: 1rem;
    color: #64748b;
}

.listener-album__summary {
    margin-top: 0.95rem;
    font-size: 0.92rem;
    line-height: 1.75;
    color: #64748b;
    cursor: pointer;
}

.listener-album__chips {
    margin-top: 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
}

.listener-album__chip {
    min-height: 2.1rem;
    padding: 0.45rem 0.9rem;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.82);
    color: #334155;
    font-size: 0.82rem;
    font-weight: 700;
    box-shadow: 0 8px 22px rgba(148, 163, 184, 0.1);
}

.listener-album__chip--primary {
    background: #111827;
    color: #fff;
}

.listener-album__chip--button {
    cursor: pointer;
}

.listener-album__actions {
    margin-top: 1.35rem;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.7rem;
}

.listener-album__play-button,
.listener-album__action-button {
    min-height: 3.45rem;
    border: 0;
    border-radius: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    font-weight: 700;
}

.listener-album__play-button {
    background: #111827;
    color: #fff;
    box-shadow: 0 18px 30px rgba(15, 23, 42, 0.24);
}

.listener-album__action-button {
    background: rgba(255, 255, 255, 0.82);
    color: #0f172a;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
}

.listener-album__play-button svg,
.listener-album__action-button svg {
    width: 1.1rem;
    height: 1.1rem;
}

.listener-album__display-switch-wrap {
    margin: 1.15rem auto 0;
    width: 100%;
    display: flex;
    justify-content: center;
}

.listener-album__display-switch {
    padding: 0.28rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
    width: fit-content;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.28rem;
}

.listener-album__display-pill {
    min-width: 4.7rem;
    height: 2.2rem;
    padding: 0 1rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 700;
}

.listener-album__display-pill.is-active {
    background: #111827;
    color: #fff;
}

.listener-album__tracks {
    margin-top: 1.35rem;
}

.listener-album__panel-header {
    margin-bottom: 1rem;
}

.listener-album__panel-eyebrow {
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #94a3b8;
}

.listener-album__panel-title {
    margin-top: 0.3rem;
    font-size: 1.2rem;
    font-weight: 800;
    color: #111827;
}

.listener-album__overview-card {
    padding: 1rem;
    border-radius: 1.3rem;
    background: rgba(255, 255, 255, 0.76);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.1);
    backdrop-filter: blur(14px);
}

.listener-album__overview-label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.12em;
}

.listener-album__overview-text {
    margin-top: 0.75rem;
    color: #475569;
    line-height: 1.8;
    white-space: pre-wrap;
    word-break: break-word;
}

.listener-album__info-list {
    margin-top: 0.75rem;
    display: grid;
    gap: 0.8rem;
}

.listener-album__info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.94rem;
}

.listener-album__info-row span {
    color: #94a3b8;
}

.listener-album__info-row a,
.listener-album__info-row strong {
    color: #0f172a;
    font-weight: 700;
    text-align: right;
}

</style>
